import { motion } from "framer-motion";
import { useState } from "react";
import { Users, Zap, ShieldCheck, Eye, Wallet, Headset, ArrowRight, Truck } from "lucide-react";
import CarrierPartnerModal from "./CarrierPartnerModal";

const CARDS = [
  { icon: Users, title: "10,000+ vetted carriers", desc: "Pre-qualified, insured and safety-scored partners on tap for any lane." },
  { icon: Zap, title: "Same-day capacity", desc: "Cover demand spikes, backhauls and one-off shipments — fast." },
  { icon: ShieldCheck, title: "Compliance & claims", desc: "We handle carrier vetting, insurance verification and claims resolution." },
  { icon: Eye, title: "Real-time visibility", desc: "One dashboard for both our own fleet and brokered loads." },
  { icon: Wallet, title: "Transparent margins", desc: "Market-based rates with no hidden fees — you see the full picture." },
  { icon: Headset, title: "A dedicated broker", desc: "One point of contact who actually knows your freight and lanes." },
];

export default function Brokerage() {
  const [carrierOpen, setCarrierOpen] = useState(false);
  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="brokerage" data-testid="brokerage-section" className="relative py-28 md:py-40 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <span className="font-mono-x text-primary text-xs tracking-[0.35em]">/ 03 — FREIGHT BROKERAGE</span>
            <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-4xl md:text-5xl leading-[0.95]">
              More than a carrier —<br /><span className="text-primary">your freight broker.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-lg text-muted-foreground leading-relaxed">
              Beyond our own fleet, Conexa Relay brokers capacity across a vetted network of thousands of carriers.
              When a lane, a deadline or a demand spike needs coverage, we source it, price it and manage it end to end —
              asset and non-asset, one accountable partner.
            </p>
          </div>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <motion.div key={c.title} data-testid={`broker-card-${i}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.33, 1, 0.68, 1] }}
              className="group rounded-3xl border border-border bg-card/40 p-7 hover:border-primary/50 transition-colors">
              <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <c.icon className="w-5 h-5" />
              </span>
              <h3 className="font-heading font-bold text-lg">{c.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <button data-testid="brokerage-quote-btn" onClick={() => go("#contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-8 py-4 text-sm hover:shadow-[0_0_40px_hsl(var(--primary)/0.5)] transition-shadow">
            Get a brokerage quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button data-testid="brokerage-carrier-btn" onClick={() => setCarrierOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-border font-semibold px-8 py-4 text-sm hover:bg-secondary transition-colors">
            <Truck className="w-4 h-4" /> Carriers: partner with us
          </button>
        </div>
      </div>
      <CarrierPartnerModal open={carrierOpen} onOpenChange={setCarrierOpen} />
    </section>
  );
}
